import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEur, fmtDate } from "@/lib/api";
import { PageHeader, Section, EmptyState, Pill } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus, Download, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { value: "directo", label: "Venta directa" },
  { value: "distribuidor", label: "Envío a distribuidor" },
  { value: "feria", label: "Feria" },
  { value: "online", label: "Online" },
];

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dists, setDists] = useState([]);
  const [whs, setWhs] = useState([]);
  const [show, setShow] = useState(false);

  async function load() {
    const [i, b, c, d, w] = await Promise.all([
      api.get("/invoices"), api.get("/books"), api.get("/customers"), api.get("/distributors"), api.get("/warehouses"),
    ]);
    setItems(i.data); setBooks(b.data); setCustomers(c.data); setDists(d.data); setWhs(w.data);
  }
  useEffect(() => { load(); }, []);

  function entityName(inv) {
    if (inv.customer_id) return customers.find(c => c.id === inv.customer_id)?.name || "—";
    if (inv.distributor_id) return dists.find(d => d.id === inv.distributor_id)?.name || "—";
    return "—";
  }

  async function downloadFile(iid, kind) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${iid}/export.${kind}`;
    const token = localStorage.getItem("access_token");
    const r = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const b = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(b);
    a.download = `factura.${kind}`; a.click();
  }

  async function del(id) {
    if (!window.confirm("¿Eliminar factura?")) return;
    await api.delete(`/invoices/${id}`); load();
  }

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Facturas exportables"
        subtitle="Crea facturas básicas y exporta en PDF o CSV. La contabilidad la llevas aparte."
        actions={
          <button onClick={() => setShow(true)} data-testid="new-invoice-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2">
            <Plus size={16} strokeWidth={1.5} /> Nueva factura
          </button>
        }
      />

      <Section>
        {items.length === 0 ? <EmptyState title="Sin facturas" description="Crea tu primera factura para una librería, distribuidor o feria." testid="invoices-empty" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                <th className="text-left px-5 py-3 font-medium">Número</th>
                <th className="text-left px-5 py-3 font-medium">Fecha</th>
                <th className="text-left px-5 py-3 font-medium">Cliente / Destino</th>
                <th className="text-left px-5 py-3 font-medium">Canal</th>
                <th className="text-right px-5 py-3 font-medium">Subtotal</th>
                <th className="text-right px-5 py-3 font-medium">IVA</th>
                <th className="text-right px-5 py-3 font-medium">Total</th>
                <th className="text-right px-5 py-3 font-medium">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map(inv => (
                  <tr key={inv.id} className="border-t border-line hover:bg-sidebar/30" data-testid={`invoice-row-${inv.number}`}>
                    <td className="px-5 py-3 font-mono text-xs">{inv.number}</td>
                    <td className="px-5 py-3 text-ink2">{fmtDate(inv.date)}</td>
                    <td className="px-5 py-3">{entityName(inv)}</td>
                    <td className="px-5 py-3"><Pill tone="muted">{inv.channel}</Pill></td>
                    <td className="px-5 py-3 text-right font-mono">{fmtEur(inv.subtotal)}</td>
                    <td className="px-5 py-3 text-right font-mono text-ink2">{fmtEur(inv.tax)}</td>
                    <td className="px-5 py-3 text-right font-mono font-medium">{fmtEur(inv.total)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => downloadFile(inv.id, "pdf")} data-testid={`invoice-pdf-${inv.number}`} className="text-xs text-ink2 hover:text-brand mr-3 inline-flex items-center gap-1"><FileText size={13} /> PDF</button>
                      <button onClick={() => downloadFile(inv.id, "csv")} data-testid={`invoice-csv-${inv.number}`} className="text-xs text-ink2 hover:text-brand mr-3 inline-flex items-center gap-1"><Download size={13} /> CSV</button>
                      <button onClick={() => del(inv.id)} className="text-xs text-crimson hover:opacity-80 inline-flex items-center gap-1"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {show && <InvoiceForm books={books} customers={customers} dists={dists} whs={whs} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function InvoiceForm({ books, customers, dists, whs, onClose, onSaved }) {
  const [channel, setChannel] = useState("directo");
  const [customer_id, setC] = useState("");
  const [distributor_id, setD] = useState("");
  const [from_warehouse_id, setWh] = useState("");
  const [create_stock_movement, setCsm] = useState(false);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ book_id: "", quantity: 1, unit_price: 0, discount_pct: 0, tax_rate: 4 }]);

  function setLine(i, k, v) { setLines(L => L.map((l, idx) => idx === i ? { ...l, [k]: v } : l)); }
  function addLine() { setLines(L => [...L, { book_id: "", quantity: 1, unit_price: 0, discount_pct: 0, tax_rate: 4 }]); }
  function delLine(i) { setLines(L => L.filter((_, idx) => idx !== i)); }

  function onBookPick(i, bid) {
    const b = books.find(x => x.id === bid);
    setLine(i, "book_id", bid);
    if (b) { setLine(i, "unit_price", b.pvp); setLine(i, "tax_rate", b.tax_rate || 4); }
  }

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    lines.forEach(l => {
      const gross = (parseFloat(l.unit_price) || 0) * (parseInt(l.quantity) || 0);
      const net = gross * (1 - (parseFloat(l.discount_pct) || 0) / 100);
      sub += net; tax += net * ((parseFloat(l.tax_rate) || 0) / 100);
    });
    return { sub, tax, total: sub + tax };
  }, [lines]);

  async function submit(e) {
    e.preventDefault();
    const valid = lines.filter(l => l.book_id && (parseInt(l.quantity) || 0) > 0);
    if (valid.length === 0) { toast.error("Añade al menos una línea válida"); return; }
    try {
      await api.post("/invoices", {
        channel,
        customer_id: customer_id || null,
        distributor_id: distributor_id || null,
        notes, status: "emitida",
        lines: valid.map(l => ({
          book_id: l.book_id,
          quantity: parseInt(l.quantity) || 0,
          unit_price: parseFloat(l.unit_price) || 0,
          discount_pct: parseFloat(l.discount_pct) || 0,
          tax_rate: parseFloat(l.tax_rate) || 0,
        })),
        create_stock_movement,
        from_warehouse_id: from_warehouse_id || null,
      });
      toast.success("Factura creada");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  return (
    <Modal title="Nueva factura" onClose={onClose} size="xl">
      <form onSubmit={submit} className="space-y-5" data-testid="invoice-form">
        <div className="grid grid-cols-4 gap-4">
          <F label="Canal">
            <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="invoice-channel" className={IN}>
              {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </F>
          <F label="Cliente">
            <select value={customer_id} onChange={(e) => setC(e.target.value)} data-testid="invoice-customer" className={IN}>
              <option value="">— Ninguno —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <F label="Distribuidor">
            <select value={distributor_id} onChange={(e) => setD(e.target.value)} className={IN}>
              <option value="">— Ninguno —</option>
              {dists.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </F>
          <F label="Salida desde almacén">
            <select value={from_warehouse_id} onChange={(e) => setWh(e.target.value)} className={IN}>
              <option value="">— No generar movimiento —</option>
              {whs.map(w => <option key={w.id} value={w.id}>{w.name} ({w.type})</option>)}
            </select>
          </F>
        </div>

        <label className="inline-flex items-start gap-2 text-sm text-ink bg-amber/10 border border-amber/30 rounded-sm px-3 py-2">
          <input type="checkbox" checked={create_stock_movement} onChange={(e) => setCsm(e.target.checked)} className="mt-0.5" />
          <span>
            <strong>Generar movimiento de stock automáticamente</strong>
            <span className="block text-xs text-ink2 mt-0.5">
              Desactívalo si ya has registrado a mano el movimiento correspondiente, para evitar duplicar unidades.
              Al borrar la factura se borran también los movimientos que ella generó.
            </span>
          </span>
        </label>

        <div className="border border-line rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-sidebar/40 text-[11px] uppercase tracking-[0.1em] text-ink2">
              <th className="text-left px-3 py-2">Libro</th>
              <th className="text-right px-3 py-2 w-20">Cant.</th>
              <th className="text-right px-3 py-2 w-28">PVP</th>
              <th className="text-right px-3 py-2 w-20">Dto%</th>
              <th className="text-right px-3 py-2 w-20">IVA%</th>
              <th className="text-right px-3 py-2 w-28">Importe</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const gross = (parseFloat(l.unit_price) || 0) * (parseInt(l.quantity) || 0);
                const net = gross * (1 - (parseFloat(l.discount_pct) || 0) / 100);
                return (
                  <tr key={i} className="border-t border-line">
                    <td className="px-3 py-1">
                      <select value={l.book_id} onChange={(e) => onBookPick(i, e.target.value)} className={IN} data-testid={`invoice-line-book-${i}`}>
                        <option value="">—</option>
                        {books.map(b => <option key={b.id} value={b.id}>{b.title} · {b.isbn}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1"><input type="number" min="0" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-1"><input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-1"><input type="number" step="0.1" value={l.discount_pct} onChange={(e) => setLine(i, "discount_pct", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-1"><input type="number" step="0.1" value={l.tax_rate} onChange={(e) => setLine(i, "tax_rate", e.target.value)} className={IN + " text-right font-mono"} /></td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(net)}</td>
                    <td className="px-3 py-1 text-right"><button type="button" onClick={() => delLine(i)} className="text-crimson text-xs">×</button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-sidebar/40 border-t-2 border-line">
                <td colSpan={5} className="px-3 py-2 text-right font-medium">Subtotal</td>
                <td className="px-3 py-2 text-right font-mono">{fmtEur(totals.sub)}</td><td></td>
              </tr>
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right text-ink2">IVA</td>
                <td className="px-3 py-2 text-right font-mono text-ink2">{fmtEur(totals.tax)}</td><td></td>
              </tr>
              <tr className="border-t border-brand">
                <td colSpan={5} className="px-3 py-2 text-right font-medium">TOTAL</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-brand">{fmtEur(totals.total)}</td><td></td>
              </tr>
            </tfoot>
          </table>
          <div className="p-3 border-t border-line">
            <button type="button" onClick={addLine} className="text-xs text-brand hover:underline">+ Añadir línea</button>
          </div>
        </div>

        <F label="Notas"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={IN} /></F>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
          <button type="submit" data-testid="invoice-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Emitir factura</button>
        </div>
      </form>
    </Modal>
  );
}

const IN = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
